import type { Json } from '@/integrations/supabase/types';

export type HireCvScoringFunctionError = {
  code?: string;
  message?: string;
  retryable?: boolean;
  documentTypeValid?: boolean;
  duplicateFound?: boolean;
  duplicate?: Json;
};

type ErrorPayload = {
  code?: unknown;
  error?: unknown;
  message?: unknown;
  retryable?: unknown;
  document_type_valid?: unknown;
  duplicate_found?: unknown;
  duplicate?: unknown;
};

const GENERIC_FUNCTION_ERROR = 'Edge Function returned a non-2xx status code';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isJson(value: unknown): value is Json {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(isJson);
  return isRecord(value) && Object.values(value).every((item) => item === undefined || isJson(item));
}

function readText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function normalizePayload(payload: ErrorPayload | null): HireCvScoringFunctionError {
  const code = readText(payload?.code);
  const rawMessage = readText(payload?.error) || readText(payload?.message);
  const message = rawMessage === GENERIC_FUNCTION_ERROR ? undefined : rawMessage;
  const normalized: HireCvScoringFunctionError = {
    code,
    message,
    retryable: typeof payload?.retryable === 'boolean' ? payload.retryable : undefined,
    documentTypeValid: typeof payload?.document_type_valid === 'boolean' ? payload.document_type_valid : undefined,
  };
  if (payload?.duplicate_found === true && isRecord(payload.duplicate) && isJson(payload.duplicate)) {
    normalized.duplicateFound = true;
    normalized.duplicate = payload.duplicate;
  }
  return normalized;
}

export class HireCvScoringError extends Error {
  readonly code?: string;
  readonly retryable?: boolean;
  readonly documentTypeValid?: boolean;

  constructor(details: HireCvScoringFunctionError, fallbackMessage: string) {
    super(details.message || fallbackMessage);
    this.name = 'HireCvScoringError';
    this.code = details.code;
    this.retryable = details.retryable;
    this.documentTypeValid = details.documentTypeValid;
  }
}

export async function readHireCvScoringFunctionError(error: unknown): Promise<HireCvScoringFunctionError> {
  if (isRecord(error)) {
    const context = error.context;
    if (context && typeof (context as { clone?: unknown }).clone === 'function') {
      const payload = await (context as Response).clone().json().catch(() => null) as ErrorPayload | null;
      if (payload) return normalizePayload(payload);
    }

    if (isRecord(error.details)) {
      return normalizePayload(error.details as ErrorPayload);
    }

    return normalizePayload({ message: error.message });
  }

  return normalizePayload({ message: error });
}
